<script setup lang="ts">
    import { inject, onMounted, reactive, watch, computed } from 'vue';
    import List from '@shared/components/List/List.vue';
    import BSFormSearch from '@shared/components/BSFormSearch.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { debounce } from '@shared/utils';
    import Loading from '@shared/components/Loading.vue';
    import { formatDate } from '@cwd/Shared/utils/formatDateTime';
    import { createUser, UserAccess } from './User.access';
    import { useRoute, useRouter } from 'vue-router';
    import Modal from '@shared/components/Modal.vue';
    import { LanguagesAccess, Language } from '@shared/Root/Languages.access';
    import { UserRegistration } from './UserRegistration';
    import AddAccountToUser from './AddAccountToUser.vue';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { BackOfficeUser } from './BackOfficeUser';
    import { AccountListItem } from './AccountReference';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { FormProps } from '@shared/components/Form/Form';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const userAccess: UserAccess = injectorAccess.injector.get(UserAccess);
    const route = useRoute();
    const router = useRouter();
    const languagesAccess: LanguagesAccess = injectorAccess.injector.get(LanguagesAccess);

    const createUserState = reactive({
        showModal: false,
        loading: false,
        accounts: [] as AccountListItem[],
        userForm: {
            disableSubmit: false,
            alert: null,
            data: {
                Email: {
                    modelValue: '',
                    required: true,
                    input: {
                        label: 'Email',
                        type: 'email',
                    },
                },
                LanguageId: {
                    modelValue: 'sv',
                    required: true,
                    select: {
                        label: 'Language',
                        options: [] as any[],
                    },
                },
                RoleType: {
                    modelValue: 0,
                    required: true,
                    select: {
                        label: 'Role',
                        options: [
                            { value: '0', label: 'Administrator' },
                            { value: '1', label: 'User' },
                        ],
                    },
                },
            },
        } as FormProps,
    });

    const isFormValid = computed(() => {
        if (!createUserState.userForm.data.Email) return false;
        const { Email, LanguageId } = createUserState.userForm.data;
        return Email.modelValue && LanguageId.modelValue && createUserState.accounts.length > 0;
    });

    onMounted(async () => {
        const searchParam = route.query.s as string;
        if (searchParam) {
            userAccess.state.filter = searchParam;
        }

        if (userAccess.state.users.length === 0) {
            await userAccess.getUsers().then(() => {
                focusSearchInput();
            });
        }

        focusSearchInput();
    });

    watch(
        () => userAccess.state.filter,
        (newVal, oldVal) => {
            if (oldVal === undefined) return;

            debounce(
                async () => {
                    router.replace({ query: { ...route.query, s: newVal } });
                    await userAccess.filterChanged();
                },
                { delay: 300 }
            );
        },
        { immediate: true }
    );

    function focusSearchInput(): void {
        setTimeout(() => {
            document.getElementById('user-list-search')?.focus();
        }, 50);
    }

    async function openCreateUserModal() {
        createUserState.showModal = true;
        if (createUserState.userForm.data.LanguageId.select?.options.length === 0) {
            await languagesAccess.getLanguages().then((languages) => {
                createUserState.userForm.data.LanguageId.select!.options = languages.map((language) => ({
                    value: language.LanguageId.toString(),
                    label: language.Name,
                }));
            });
        }
    }

    async function create() {
        createUserState.loading = true;
        const newUser: UserRegistration = {
            Email: createUserState.userForm.data.Email.modelValue,
            LanguageId: createUserState.userForm.data.LanguageId.modelValue,
            Accounts: createUserState.accounts,
            RoleType: createUserState.userForm.data.RoleType.modelValue,
            FirstName: '',
            LastName: '',
        };

        await createUser(newUser)
            .then((createdUser: BackOfficeUser) => {
                bsAlert({ title: 'Success', content: 'User created successfully' });
                createUserState.userForm.data.Email.modelValue = '';
                userAccess.getUsers(true);

                router.push({ name: 'UserEdit', params: { userId: createdUser.UserId } });
            })
            .catch((error) => {
                bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to create user' });
            })
            .finally(() => {
                createUserState.loading = false;
            });
    }

    function addAccount(account: AccountListItem) {
        createUserState.accounts.push(account);
    }
</script>

<template>
    <List
        displayAs="table"
        itemId="UserId"
        title="Users"
        :allLoaded="userAccess.state.allItemsLoaded"
        :totalItemCount="userAccess.state.usersCount"
        :perPage="20"
        :items="userAccess.state.users"
        :tableHeaders="['Email', 'Name', 'Language', 'Account', 'Created', 'Last Login']"
        :loading="userAccess.state.loadingUsers"
        :filterKeys="['FirstName', 'LastName', 'Email', 'MobilePhone', 'Status']"
        :orderOptions="[
            { key: 'Created', display: 'Created' },
            { key: 'FirstName', display: 'First Name' },
            { key: 'LastName', display: 'Last Name' },
            { key: 'Email', display: 'Email' },
            { key: 'Status', display: 'Status' },
            { key: 'LastLogin', display: 'Last Login' },
        ]"
        :serverSideFilter="true"
        :hideSearch="true"
        @loadMore="userAccess.loadMoreItems"
        @update:order="(orderByValue, orderByDirection) => userAccess.orderChanged(orderByValue, orderByDirection)">
        <template #search>
            <BSFormSearch
                v-model="userAccess.state.filter"
                class="search-input-width"
                inputId="user-list-search"
                :disabled="false"
                @reset="() => (userAccess.state.filter = '')"></BSFormSearch>
        </template>
        <template #toolbar>
            <button class="btn btn-primary" @click="openCreateUserModal">Create User</button>
            <button class="btn btn-default" @click="userAccess.getUsers(true)">Refresh</button>
        </template>
        <template #item="{ item: user, index: index }">
            <td>
                <i class="fa fa-square m-sm-r" :class="userAccess.getStatusClass(user.StatusName)" aria-hidden="true"></i>
                <router-link :to="{ name: 'UserEdit', params: { userId: user.UserId } }">
                    {{ user.Email == null || user.Email == '' ? 'No email' : user.Email }}
                </router-link>
            </td>
            <td>{{ user.FirstName }} {{ user.LastName }}</td>
            <td>{{ user.LanguageId }}</td>
            <td>{{ user.LastAccountName }}</td>
            <td>
                {{ formatDate(user.Created) }}
            </td>
            <td>
                {{ formatDate(user.LastLogin) }}
            </td>
        </template>
    </List>

    <Modal :show="createUserState.showModal" @close="() => (createUserState.showModal = false)" title="Create User" size="lg">
        <template #content>
            <Loading :show="createUserState.loading" loadingStyle="progress-bar"></Loading>
            <div v-if="!createUserState.loading">
                <Form
                    :data="createUserState.userForm.data"
                    :submitting="createUserState.loading"
                    :hide-submit="true"
                    @update:isValid="(key, newVal) => (createUserState.userForm.data[key].isValid = newVal)"
                    @update:modelValue="(key, newVal) => (createUserState.userForm.data[key].modelValue = newVal)"></Form>

                <AddAccountToUser :user-accounts="createUserState.accounts" @add-account="addAccount" />
            </div>
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" @click="create" :disabled="!isFormValid || createUserState.loading">Create</button>
        </template>
    </Modal>
</template>
